import { useState, useEffect, useCallback } from 'react';
import { X, Loader as Loader2, CircleCheck as CheckCircle2, Download, ChevronRight, CircleAlert as AlertCircle, Plus, Link2, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { bankAccountsService } from '../../lib/bankAccountsService';

// ─── Types ───────────────────────────────────────────────────────────────────

interface MatchedAccount {
  bankAccountId: string;
  nickname: string;
  bankName: string;
  branchNumber: string | null;
  accountNumber: string;
  financyAccountId: string;
  financyAccountName: string | null;
  accountType: string | null;
  currency: string;
  lastPulledDate: string | null;
}

interface UnmatchedFinancyAccount {
  financyAccountId: string;
  financyAccountName: string | null;
  accountType: string | null;
  bankCode: string | null;
  branchNumber: string | null;
  accountNumber: string | null;
  currency: string;
}

interface ExistingUnlinkedAccount {
  id: string;
  nickname: string;
  bankName: string;
  branchNumber: string | null;
  accountNumber: string | null;
  currency: string;
}

interface PreviewAccount {
  bankAccountId: string;
  nickname: string;
  dateFrom: string;
  dateTo: string;
  newCount: number;
  skippedCount: number;
  sample: Array<{ date: string; description: string; amount: number }>;
}

interface FinancyPullModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess: (importedCount: number, newTransactionIds?: string[]) => void;
}

type Step = 'select' | 'preview';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const todayIso = () => new Date().toISOString().slice(0, 10);

const oneYearAgoIso = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
};

const fmtAmount = (n: number, currency: string) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: currency || 'ILS' }).format(n);

const BANK_CODES: Record<string, string> = {
  '10': 'לאומי',
  '11': 'דיסקונט',
  '12': 'הפועלים',
  '13': 'אגוד',
  '14': 'אוצר החייל',
  '17': 'מרכנתיל',
  '20': 'מזרחי טפחות',
  '31': 'הבינלאומי',
  '46': 'מסד',
  '52': 'פאגי',
  '54': 'ירושלים',
};

function bankNameFromCode(code: string | null): string {
  if (!code) return '';
  const clean = code.replace(/\D/g, '');
  return BANK_CODES[clean] ?? `בנק ${code}`;
}

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  CHECKING: 'עו"ש',
  SAVINGS: 'חיסכון',
  CREDIT_CARD: 'כרטיס אשראי',
  LOAN: 'הלוואה',
  MORTGAGE: 'משכנתא',
};

function accountTypeLabel(type: string | null): string {
  if (!type) return '';
  return ACCOUNT_TYPE_LABELS[type] ?? type;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function FinancyPullModal({ isOpen, onClose, companyId, onSuccess }: FinancyPullModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accounts, setAccounts] = useState<MatchedAccount[]>([]);
  const [unmatchedAccounts, setUnmatchedAccounts] = useState<UnmatchedFinancyAccount[]>([]);
  const [existingUnlinked, setExistingUnlinked] = useState<ExistingUnlinkedAccount[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState(todayIso());
  // Per-account dateFrom overrides for newly added accounts
  const [perAccountDateFrom, setPerAccountDateFrom] = useState<Record<string, string>>({});
  const [previewData, setPreviewData] = useState<PreviewAccount[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which unmatched account is currently showing "link to existing" dropdown
  const [linkingAccountId, setLinkingAccountId] = useState<string | null>(null);
  // Track which accounts were just added (to show dateFrom picker)
  const [newlyAddedIds, setNewlyAddedIds] = useState<Set<string>>(new Set());
  // Track busy state per unmatched account
  const [addingAccountId, setAddingAccountId] = useState<string | null>(null);
  // Debug info from backend
  const [debugInfo, setDebugInfo] = useState<Record<string, unknown> | null>(null);

  const authHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      Authorization: `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    setError(null);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financy-list-accounts`,
        { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ company_id: companyId }) },
      );
      const result = await res.json().catch(() => null);
      if (!res.ok || !result || result.error) {
        setError(result?.error || `טעינת החשבונות נכשלה (${res.status})`);
        setAccounts([]);
        setUnmatchedAccounts([]);
        setExistingUnlinked([]);
        return;
      }
      const matched: MatchedAccount[] = result.matchedAccounts || [];
      setAccounts(matched);
      setUnmatchedAccounts(result.unmatchedAccounts || []);
      setExistingUnlinked(result.existingUnlinkedAccounts || []);
      setSelectedIds(new Set(matched.map((a) => a.bankAccountId)));
      setDateFrom(result.suggestedDateFrom || '');
      if (result._debug) setDebugInfo(result._debug);
    } catch (err) {
      console.error('Financy list accounts error:', err);
      setError('טעינת החשבונות נכשלה. נסו שוב מאוחר יותר.');
      setAccounts([]);
      setUnmatchedAccounts([]);
      setExistingUnlinked([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, [authHeaders, companyId]);

  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setPreviewData(null);
      setError(null);
      setDateTo(todayIso());
      setPerAccountDateFrom({});
      setNewlyAddedIds(new Set());
      setLinkingAccountId(null);
      setAddingAccountId(null);
      loadAccounts();
    }
  }, [isOpen, loadAccounts]);

  if (!isOpen) return null;

  const toggleAccount = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Add unmatched Financy account as a new bank account in the system
  const handleAddAsNew = async (fa: UnmatchedFinancyAccount) => {
    setAddingAccountId(fa.financyAccountId);
    setError(null);
    try {
      const bankName = bankNameFromCode(fa.bankCode) || fa.financyAccountName || 'חשבון בנק';
      const nickname = fa.financyAccountName || `${bankName} ${fa.accountNumber || ''}`.trim();

      const isCreditCard = fa.accountType === 'CREDIT_CARD';
      const { data, error: createErr } = await bankAccountsService.createBankAccount({
        company_id: companyId,
        nickname,
        bank_name: bankName,
        account_number: fa.accountNumber || '',
        branch_number: fa.branchNumber || null,
        currency: fa.currency || 'ILS',
        is_active: true,
        account_type: isCreditCard ? 'credit_card' : 'checking',
        card_provider: isCreditCard ? (fa.financyAccountName || null) : null,
      });

      if (createErr || !data) {
        const msg = (createErr as any)?.message || 'שגיאה ביצירת חשבון הבנק';
        if (msg.includes('duplicate') || msg.includes('unique')) {
          setError('חשבון עם מספר זהה כבר קיים במערכת. השתמשו ב"קשר לקיים" במקום.');
        } else {
          setError(msg);
        }
        return;
      }

      // Move from unmatched to matched
      const newMatched: MatchedAccount = {
        bankAccountId: data.id,
        nickname: data.nickname,
        bankName: data.bank_name,
        branchNumber: data.branch_number,
        accountNumber: data.account_number,
        financyAccountId: fa.financyAccountId,
        financyAccountName: fa.financyAccountName,
        accountType: fa.accountType,
        currency: fa.currency || data.currency,
        lastPulledDate: null,
      };

      setAccounts((prev) => [...prev, newMatched]);
      setSelectedIds((prev) => new Set([...prev, data.id]));
      setUnmatchedAccounts((prev) => prev.filter((u) => u.financyAccountId !== fa.financyAccountId));
      setNewlyAddedIds((prev) => new Set([...prev, data.id]));
      setPerAccountDateFrom((prev) => ({ ...prev, [data.id]: '' }));
    } catch (err) {
      setError((err as Error).message || 'שגיאה ביצירת החשבון');
    } finally {
      setAddingAccountId(null);
    }
  };

  // Link unmatched Financy account to an existing bank account
  const handleLinkToExisting = async (fa: UnmatchedFinancyAccount, existingAccountId: string) => {
    setAddingAccountId(fa.financyAccountId);
    setError(null);
    try {
      // Update the existing bank account's branch/account numbers to match Financy
      const updates: Record<string, string> = {};
      if (fa.accountNumber) updates.account_number = fa.accountNumber;
      if (fa.branchNumber) updates.branch_number = fa.branchNumber;

      if (Object.keys(updates).length > 0) {
        const { error: updateErr } = await bankAccountsService.updateBankAccount(existingAccountId, updates);
        if (updateErr) {
          setError((updateErr as any)?.message || 'שגיאה בעדכון פרטי החשבון');
          return;
        }
      }

      const linked = existingUnlinked.find((e) => e.id === existingAccountId);
      if (!linked) return;

      const newMatched: MatchedAccount = {
        bankAccountId: linked.id,
        nickname: linked.nickname,
        bankName: linked.bankName,
        branchNumber: fa.branchNumber || linked.branchNumber,
        accountNumber: fa.accountNumber || linked.accountNumber || '',
        financyAccountId: fa.financyAccountId,
        financyAccountName: fa.financyAccountName,
        accountType: fa.accountType,
        currency: fa.currency || linked.currency,
        lastPulledDate: null,
      };

      setAccounts((prev) => [...prev, newMatched]);
      setSelectedIds((prev) => new Set([...prev, linked.id]));
      setUnmatchedAccounts((prev) => prev.filter((u) => u.financyAccountId !== fa.financyAccountId));
      setExistingUnlinked((prev) => prev.filter((e) => e.id !== existingAccountId));
      setNewlyAddedIds((prev) => new Set([...prev, linked.id]));
      setPerAccountDateFrom((prev) => ({ ...prev, [linked.id]: '' }));
      setLinkingAccountId(null);
    } catch (err) {
      setError((err as Error).message || 'שגיאה בקישור החשבון');
    } finally {
      setAddingAccountId(null);
    }
  };

  const callSync = async (preview: boolean) => {
    // Build per-account dateFrom overrides for newly added accounts
    const overrides: Record<string, string> = {};
    for (const id of newlyAddedIds) {
      if (selectedIds.has(id) && perAccountDateFrom[id]) {
        overrides[id] = perAccountDateFrom[id];
      }
    }

    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financy-sync-bank`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          company_id: companyId,
          account_ids: Array.from(selectedIds),
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          accountDateOverrides: Object.keys(overrides).length > 0 ? overrides : undefined,
          preview,
        }),
      },
    );
    const result = await res.json().catch(() => null);
    if (!res.ok || !result || result.error) {
      throw new Error(result?.error || `הפעולה נכשלה (${res.status})`);
    }
    return result;
  };

  const handlePreview = async () => {
    if (selectedIds.size === 0) {
      setError('בחרו לפחות חשבון אחד למשיכה.');
      return;
    }
    // Validate that newly added accounts have a dateFrom set
    for (const id of newlyAddedIds) {
      if (selectedIds.has(id) && !perAccountDateFrom[id]) {
        setError('נא לבחור תאריך התחלה עבור החשבונות החדשים שנוספו.');
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      const result = await callSync(true);
      setPreviewData(result.perAccount || []);
      setStep('preview');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await callSync(false);
      onSuccess(Number(result.imported ?? 0), result.newTransactionIds ?? []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const totalNew = (previewData || []).reduce((s, a) => s + a.newCount, 0);
  const totalSkipped = (previewData || []).reduce((s, a) => s + a.skippedCount, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" dir="rtl">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2">
            <Download className="h-6 w-6 text-teal-600" />
            <h2 className="text-2xl font-bold text-gray-900">משיכה מבוקרת מהבנק</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="סגור">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-md p-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          )}

          {step === 'select' && (
            <>
              {loadingAccounts ? (
                <div className="flex items-center justify-center py-10 text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin ml-2" />
                  טוען חשבונות מ-Financy…
                </div>
              ) : (
                <>
                  {/* ── Matched accounts ── */}
                  {accounts.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">חשבונות מוכנים למשיכה</label>
                      <div className="space-y-2">
                        {accounts.map((acc) => (
                          <div key={acc.bankAccountId}>
                            <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(acc.bankAccountId)}
                                onChange={() => toggleAccount(acc.bankAccountId)}
                                className="h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {acc.nickname}
                                  {newlyAddedIds.has(acc.bankAccountId) && (
                                    <span className="mr-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">חדש</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {acc.bankName}
                                  {acc.branchNumber ? ` · סניף ${acc.branchNumber}` : ''}
                                  {` · חשבון ${acc.accountNumber}`}
                                  {acc.lastPulledDate ? ` · נמשך לאחרונה: ${acc.lastPulledDate}` : ' · טרם נמשך'}
                                </div>
                              </div>
                            </label>
                            {/* Per-account dateFrom for newly added accounts */}
                            {newlyAddedIds.has(acc.bankAccountId) && selectedIds.has(acc.bankAccountId) && (
                              <div className="mr-10 mt-1 mb-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                                <Calendar className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                <span className="text-xs text-amber-800 font-medium whitespace-nowrap">מאיזה תאריך למשוך?</span>
                                <input
                                  type="date"
                                  value={perAccountDateFrom[acc.bankAccountId] || ''}
                                  min={oneYearAgoIso()}
                                  max={dateTo}
                                  onChange={(e) => setPerAccountDateFrom((prev) => ({ ...prev, [acc.bankAccountId]: e.target.value }))}
                                  className="flex-1 border border-amber-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 bg-white"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Unmatched Financy accounts ── */}
                  {unmatchedAccounts.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">חשבונות שנמצאו ב-Financy ולא מוגדרים במערכת</label>
                      <p className="text-xs text-gray-500 mb-2">ניתן להוסיף אותם כחדשים או לקשר לחשבון שכבר קיים במערכת.</p>
                      <div className="space-y-2">
                        {unmatchedAccounts.map((fa) => {
                          const isAdding = addingAccountId === fa.financyAccountId;
                          const isLinking = linkingAccountId === fa.financyAccountId;
                          return (
                            <div
                              key={fa.financyAccountId}
                              className="border border-dashed border-gray-300 rounded-md p-3 bg-gray-50"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-800 truncate">
                                    {fa.financyAccountName || 'חשבון לא מזוהה'}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {fa.accountType && `${accountTypeLabel(fa.accountType)} · `}
                                    {fa.bankCode && `${bankNameFromCode(fa.bankCode)} · `}
                                    {fa.branchNumber && `סניף ${fa.branchNumber} · `}
                                    {fa.accountNumber && `חשבון ${fa.accountNumber}`}
                                    {fa.currency && fa.currency !== 'ILS' ? ` · ${fa.currency}` : ''}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <button
                                    onClick={() => handleAddAsNew(fa)}
                                    disabled={isAdding}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                                  >
                                    {isAdding ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Plus className="h-3.5 w-3.5" />
                                    )}
                                    הוסף כחדש
                                  </button>
                                  {existingUnlinked.length > 0 && (
                                    <button
                                      onClick={() => setLinkingAccountId(isLinking ? null : fa.financyAccountId)}
                                      disabled={isAdding}
                                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                                    >
                                      <Link2 className="h-3.5 w-3.5" />
                                      קשר לקיים
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Link-to-existing dropdown */}
                              {isLinking && (
                                <div className="mt-2 border-t border-gray-200 pt-2">
                                  <p className="text-xs text-gray-600 mb-1.5">בחרו את החשבון הקיים שתואם לחשבון זה:</p>
                                  <div className="space-y-1">
                                    {existingUnlinked.map((ex) => (
                                      <button
                                        key={ex.id}
                                        onClick={() => handleLinkToExisting(fa, ex.id)}
                                        disabled={isAdding}
                                        className="w-full text-right flex items-center gap-2 p-2 rounded-md hover:bg-teal-50 border border-gray-200 hover:border-teal-300 transition-colors disabled:opacity-60"
                                      >
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium text-gray-800 truncate">{ex.nickname}</div>
                                          <div className="text-xs text-gray-500">
                                            {ex.bankName}
                                            {ex.branchNumber ? ` · סניף ${ex.branchNumber}` : ''}
                                            {ex.accountNumber ? ` · חשבון ${ex.accountNumber}` : ''}
                                          </div>
                                        </div>
                                        <Link2 className="h-4 w-4 text-teal-500 flex-shrink-0" />
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Empty state */}
                  {accounts.length === 0 && unmatchedAccounts.length === 0 && (
                    <div className="text-center py-8 text-gray-600">
                      <p>לא נמצאו חשבונות ב-Financy. ודאו שחיברתם חשבונות בנק דרך ממשק Financy.</p>
                      {debugInfo && (
                        <div className="mt-3 text-xs text-gray-400 bg-gray-50 rounded p-2 text-left ltr" dir="ltr">
                          <p>Debug: found {String(debugInfo.financyAccountsFound ?? 0)} accounts from Financy</p>
                          <p>Response format: {String(debugInfo.responseFormat ?? 'n/a')}</p>
                          <p>Bank accounts in system: {String(debugInfo.bankAccountsInSystem ?? 0)}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Date range for existing (non-new) accounts */}
                  {accounts.length > 0 && (
                    <>
                      <div className="border-t border-gray-200 pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">טווח תאריכים למשיכה (חשבונות קיימים)</label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">מתאריך</label>
                            <input
                              type="date"
                              value={dateFrom}
                              min={oneYearAgoIso()}
                              max={dateTo}
                              onChange={(e) => setDateFrom(e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">עד תאריך</label>
                            <input
                              type="date"
                              value={dateTo}
                              max={todayIso()}
                              onChange={(e) => setDateTo(e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          ברירת המחדל היא מהמשיכה האחרונה ועד היום. חשבונות חדשים שנוספו כרגע ישתמשו בתאריך שבחרתם עבורם למעלה.
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {step === 'preview' && previewData && (
            <>
              <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-md p-3">
                <CheckCircle2 className="h-5 w-5 text-teal-600 flex-shrink-0" />
                <p className="text-sm font-medium text-teal-800">
                  יימשכו {totalNew} תנועות חדשות. {totalSkipped > 0 ? `${totalSkipped} תנועות כבר קיימות ולא יתווספו.` : ''}
                </p>
              </div>

              <div className="space-y-3">
                {previewData.map((acc) => (
                  <div key={acc.bankAccountId} className="border border-gray-200 rounded-md p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm font-semibold text-gray-900">{acc.nickname}</div>
                      <div className="text-xs text-gray-500">{acc.dateFrom} — {acc.dateTo}</div>
                    </div>
                    <div className="text-xs text-gray-600 mb-2">
                      {acc.newCount} חדשות · {acc.skippedCount} קיימות
                    </div>
                    {acc.sample.length > 0 && (
                      <ul className="space-y-1">
                        {acc.sample.map((s, i) => (
                          <li key={i} className="flex items-center justify-between text-xs text-gray-700 border-t border-gray-100 pt-1">
                            <span className="truncate ml-2">{s.date} · {s.description}</span>
                            <span className={s.amount < 0 ? 'text-red-600 font-medium' : 'text-green-700 font-medium'}>
                              {fmtAmount(s.amount, 'ILS')}
                            </span>
                          </li>
                        ))}
                        {acc.newCount > acc.sample.length && (
                          <li className="text-xs text-gray-400 pt-1">ועוד {acc.newCount - acc.sample.length} תנועות…</li>
                        )}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          {step === 'select' ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
                ביטול
              </button>
              <button
                onClick={handlePreview}
                disabled={busy || loadingAccounts || accounts.length === 0 || selectedIds.size === 0}
                className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 className="h-4 w-4 ml-2 animate-spin" /> : <ChevronRight className="h-4 w-4 ml-1" />}
                תצוגה מקדימה
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { setStep('select'); setError(null); }}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                חזרה
              </button>
              <button
                onClick={handleConfirm}
                disabled={busy || totalNew === 0}
                className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 className="h-4 w-4 ml-2 animate-spin" /> : <Download className="h-4 w-4 ml-2" />}
                אשר משיכה ({totalNew})
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
